<template>
  <v-bottom-sheet
    v-model="showReviewsMenu"
    inset
    class="rounded"
    width="90%"
    v-if="$route.name !== 'NoAccess' && $route.name !== 'AccessDenied'"
  >
    <v-list density="comfortable">
      <v-toolbar density="compact" color="transparent">
        <v-toolbar-title>
          {{ reviewsTitle }}
        </v-toolbar-title>
        <template v-slot:append>
          <v-btn icon size="large" @click="showReviewsMenu = false">
            <v-icon>mdi-close</v-icon>
            <v-tooltip activator="parent" location="bottom">Close</v-tooltip>
          </v-btn>
        </template>
      </v-toolbar>

      <v-divider></v-divider>

      <!-- Reviews home landing page -->
      <v-list-item
        prepend-icon="mdi-home"
        title="Reviews Home"
        subtitle="Reviews landing page"
        @click="goToHome"
      />
      <!-- Today's daily assignments -->
      <v-list-item
        prepend-icon="mdi-calendar-today"
        title="Today's Assignments"
        subtitle="View and complete today's reviews"
        @click="goToTodaysAssignments"
      />
      <!-- Weekly assignment calendar -->
      <v-list-item
        prepend-icon="mdi-calendar-month"
        title="Assignment Calendar"
        subtitle="Weekly assignment calendar view"
        @click="openCalendar"
      />
      <!-- Coverage report dialog -->
      <v-list-item
        prepend-icon="mdi-chart-box-outline"
        title="Coverage Report"
        subtitle="Check review coverage status"
        @click="openCoverageReport"
      />
      <!-- Export current week schedule as PDF -->
      <v-list-item
        prepend-icon="mdi-file-pdf-box"
        title="Export Schedule PDF"
        subtitle="Current week schedule report"
        @click="onExportPdf"
      />
      <!-- Cost report PDF -->
      <v-list-item
        prepend-icon="mdi-file-pdf-box"
        title="Cost Report PDF"
        subtitle="Platform costs for a date range"
        @click="showReviewsMenu = false; costReportDialogOpen = true"
      />

      <v-divider></v-divider>

      <!-- Manage assignment rules (admin) -->
      <v-list-item
        prepend-icon="mdi-table"
        title="Manage Assignments"
        subtitle="View and edit all assignment entries"
        @click="goToManageAssignments"
      />
      <!-- Reviews CRUD management (admin) -->
      <v-list-item
        prepend-icon="mdi-database-edit-outline"
        title="Reviews Management"
        subtitle="Add, edit, and delete reviews"
        @click="goToManagement"
      />
      <!-- Bulk import reviews (admin) -->
      <v-list-item
        prepend-icon="mdi-file-upload"
        title="Import Reviews"
        subtitle="Bulk import from file"
        @click="goToImport"
      />
    </v-list>
  </v-bottom-sheet>

  <!-- Dialog Components -->
  <ReviewsCoverageReportDialog />

  <!-- Cost Report Date Range Dialog -->
  <BaseDialog
    v-model="costReportDialogOpen"
    title="Cost Report"
    title-icon="mdi-file-pdf-box"
    :max-width="400"
    save-text="Generate PDF"
    :save-loading="exportingCost"
    :save-disabled="!costDateFrom || !costDateTo"
    @save="onExportCostPdf"
  >
    <div class="text-caption text-medium-emphasis mb-3">Select the date range for the cost report</div>
    <DateRangeFilter
      v-model:from="costDateFrom"
      v-model:to="costDateTo"
      :clearable="false"
      stacked
      @change="({ from }) => onCostDateFromChange(from)"
    />
  </BaseDialog>
</template>

<script setup>
import { ref } from "vue";
import { useRouter } from "vue-router";
import { useDisplay } from "vuetify";
import { useApp } from "@/composables/useApp.js";
import { useReviews } from "@/composables/useReviews.js";
import api from "@/utils/api.js";
import { toLocalYMD, toLocalYM } from "@/utils/dateLocal.js";
import ReviewsCoverageReportDialog from "@/components/ReviewsCoverageReportDialog.vue";
import BaseDialog from "@/components/BaseDialog.vue";
import DateRangeFilter from "@/components/DateRangeFilter.vue";
import { periodRange } from "@/utils/datePresets.js";

const router = useRouter();
const { mobile } = useDisplay();
const { showReviewsMenu, reviewsTitle } = useApp();
const { openReviewsCoverageDialog } = useReviews();

// Cost report dialog state
const costReportDialogOpen = ref(false);
// Opens on this month, in Dubai days (utils/datePresets.js).
const [_costFrom, _costTo] = periodRange("thisMonth");
const costDateFrom = ref(_costFrom);
const costDateTo = ref(_costTo);
const exportingCost = ref(false);

/** Auto-sync end date when start date changes. */
function onCostDateFromChange(val) {
  if (val && costDateFrom.value > costDateTo.value) {
    costDateTo.value = costDateFrom.value;
  }
}

/** Export cost report PDF for the selected date range. */
async function onExportCostPdf() {
  exportingCost.value = true;
  try {
    const [configRes, assignmentsRes] = await Promise.all([
      api.marketingApi.get("/api/marketing/reviews/admin/config"),
      api.marketingApi.get("/api/marketing/reviews/admin/assignments"),
    ]);
    const config = configRes.data || {};
    const entries = assignmentsRes.data.entries || [];
    const { generateReviewCostReportPdf } = await import("@/utils/reviewCostReportPdf.js");
    const result = generateReviewCostReportPdf({
      config,
      entries,
      dateFrom: costDateFrom.value,
      dateTo: costDateTo.value,
    });
    if (!result?.error) {
      costReportDialogOpen.value = false;
    }
  } catch (err) {
    console.error("Failed to export cost PDF:", err);
  } finally {
    exportingCost.value = false;
  }
}

function goToHome() {
  showReviewsMenu.value = false;
  router.push("/reviews");
}

function goToTodaysAssignments() {
  showReviewsMenu.value = false;
  router.push("/reviews/today");
}

function goToManagement() {
  showReviewsMenu.value = false;
  router.push("/settings/reviews-admin");
}

function goToImport() {
  showReviewsMenu.value = false;
  router.push("/reviews-import");
}

function openCalendar() {
  showReviewsMenu.value = false;
  if (mobile.value) {
    router.push("/reviews-calendar");
  } else {
    const route = router.resolve("/reviews-calendar");
    window.open(route.href, "_blank");
  }
}

function goToManageAssignments() {
  showReviewsMenu.value = false;
  router.push("/review-assignments");
}

function openCoverageReport() {
  showReviewsMenu.value = false;
  openReviewsCoverageDialog();
}

async function onExportPdf() {
  showReviewsMenu.value = false;
  try {
    // Fetch assignment entries and users for the PDF
    const [assignmentsRes, usersRes] = await Promise.all([
      api.marketingApi.get("/api/marketing/reviews/admin/assignments"),
      api.authApi.get("/api/admin/users"),
    ]);
    // Map assignment_date → schedule_date for PDF report compat
    const entries = (assignmentsRes.data.entries || []).map((e) => ({
      ...e,
      schedule_date: e.assignment_date,
      assigned_user_id: e.user_id,
    }));
    const usersMap = new Map();
    for (const u of usersRes.data || []) {
      const name = [u.first_name, u.last_name].filter(Boolean).join(" ");
      usersMap.set(u.id, { name: name || u.email, email: u.email });
    }
    // Use current month as default
    const selectedMonth = toLocalYM();
    const { generateReviewCalendarPdf } = await import("@/utils/reviewCalendarPdfReport.js");
    generateReviewCalendarPdf(entries, selectedMonth, usersMap);
  } catch (err) {
    console.error("Failed to export PDF:", err);
  }
}
</script>
